/**
 * Mooring tuning (M10 P1e1, hull tiers spec §6.4): `src/data/moor.json`, Zod-validated here. A hull whose band a
 * station bay doesn't take (`bands.ts bayAccepts`) is placed at, and released from, a mooring-ring point
 * (`moor.ts moorRingPoint`) instead of the bay pad. `enabled` gates the player moor flow (P1e2).
 */
import { z } from 'zod'
import moorJson from '../data/moor.json'
import { shipBandSchema } from '../data/schemas'

const positive = z.number().positive()

/**
 * Moored market services (M10 P5a8a, `space/tender.ts`): one batched tender run per visit. `enabled` false (until W6,
 * review F5) keeps today: a moored hull trades at once, exactly as docked.
 */
export const tenderConfigSchema = z.strictObject({
  comment: z.string().optional(),
  enabled: z.boolean(),
  /** The run's base time (s). */
  tenderS: z.number().nonnegative(),
  /** Seconds added per 1,000 t queued. */
  tenderSPerKt: z.number().nonnegative(),
  /** The longest a run takes (s). */
  tenderCapS: positive,
  /** Moored repair and refit take this many times the docked time (no extra credit). */
  serviceTimeMult: z.number().min(1),
  /** The run's wall-clock step (s): the sim holds while docked, so a sent run steps on a timer. */
  tickS: positive,
})

export type TenderConfig = z.infer<typeof tenderConfigSchema>

/**
 * Transfers between your own ships at one station (M10 P5a8b, `fleet-link/transfer.ts`): cargo lots and locker
 * modules move for free between the flown ship and another of your ships docked or moored at the same station (or
 * the carried pair). `enabled` false (until W6) shows no transfer panel: two owned ships at one station is already
 * possible today (M6 fleets), so the panel is gated by data, not by the situation. Absent = off.
 */
export const transfersConfigSchema = z.strictObject({
  comment: z.string().optional(),
  enabled: z.boolean(),
})

export type TransfersConfig = z.infer<typeof transfersConfigSchema>

export const moorConfigSchema = z
  .strictObject({
    comment: z.string().optional(),
    /** The player moor flow (P1e2). False: an L is refused at bays (`tooLarge`) and can't request moor. */
    enabled: z.boolean(),
    /** Moor request range from the station's hull: centre distance less the station radius (m; scale build §3.3). */
    rangeM: positive,
    /** The ring's radius round the station centre (m); raised to station radius + `ringClearM` for big stations. */
    ringRadiusM: positive,
    /** The least gap between the station's bounding radius and the ring (m). */
    ringClearM: positive,
    /** Ring slots, spread over the side away from the bay. */
    slots: z.number().int().positive(),
    /** The ring slot the player's ship loads, boards and launches at. */
    playerSlot: z.number().int().nonnegative(),
    /** No slot lies within this angle of the bay's out axis (keeps the approach corridor clear). */
    corridorClearDeg: z.number().positive().lt(180),
    holdToleranceM: positive,
    holdSpeedMps: positive,
    approachSpeedMps: positive,
    /** The share of the weakest linear acceleration the approach brakes with: speed ≤ √(2 × share × a × d) (`moorFlow.ts`). */
    brakeAccelShare: z.number().positive().max(1),
    /** Release speed from the ring on launch (m/s), radially out. */
    launchSpeedMps: positive,
    /** AI dwell at the ring before it may despawn (s). */
    aiDwellS: positive,
    /** An AI L despawns only outside this half-angle of the player's nose... */
    despawnViewHalfAngleDeg: z.number().positive().max(180),
    /** ...or beyond this range (m). */
    despawnFarM: positive,
    /** The size of a `DOCK_BAY` socket that has no `size` param. */
    defaultBaySize: shipBandSchema,
    /** Moored market services: the tender run (M10 P5a8a). */
    tender: tenderConfigSchema,
    /** Transfers between your ships at one station (M10 P5a8b). Absent = off. */
    transfers: transfersConfigSchema.optional(),
  })
  .refine((c) => c.playerSlot < c.slots, { message: 'playerSlot must be below slots' })

export type MoorConfig = z.infer<typeof moorConfigSchema>

/** Parses and validates moor.json (throws a ZodError naming the bad field). */
export function loadMoorConfig(raw: unknown = moorJson): MoorConfig {
  return moorConfigSchema.parse(raw)
}

let cached: MoorConfig | undefined

/** The parsed moor.json (cached). */
export function moorConfig(): MoorConfig {
  cached ??= loadMoorConfig()
  return cached
}
